import { collection, doc, getFirestore, query, updateDoc, where } from "firebase/firestore";
import { getAnalytics, logEvent } from "firebase/analytics";
import { httpsCallable } from "firebase/functions";
import { ProfileKeys, ReportTypes } from 'fixit-shared/FirestoreKeys';
import { downloadReports, saveReport } from './reports';

const CREATE_PROFILE_FUNCTION = 'createProfile';
const EDIT_PROFILE_FUNCTION = 'editProfile';
const DELETE_PROFILE_FUNCTION = 'deleteProfile';

const TRANSFERABLE_REPORT_TYPES = [
  { reportType: ReportTypes.Incident, fileKey: 'incidents' },
  { reportType: ReportTypes.CertificateOfAchievement, fileKey: 'certificatesOfAchievement' },
];

export const buildProfileExport = async (profile) => {
  const collections = await Promise.all(
    TRANSFERABLE_REPORT_TYPES.map(({ reportType }) => downloadReports(reportType, profile.id))
  );

  const exported = { profile };
  TRANSFERABLE_REPORT_TYPES.forEach(({ fileKey }, index) => {
    exported[fileKey] = collections[index];
  });

  return [exported];
};

export const readProfileExport = (uploadedProfile) => {
  const reportCollections = {};
  TRANSFERABLE_REPORT_TYPES.forEach(({ reportType, fileKey }) => {
    reportCollections[reportType] = uploadedProfile[fileKey];
  });

  return reportCollections;
};

export const getProfilesRef = (uid) => {
  if(!uid) return undefined;
  return query(
    collection(getFirestore(), ProfileKeys.tableName),
    where(ProfileKeys.sessionRunners, "array-contains", uid)
  );
};

const _reportAddSubjectProfileTelemetry = (userProfile,  subjectProfile, remainingProfiles) => {
  const telemetryProps = {
      profile_uuid: userProfile.id,
      chosen_profile_icon: [subjectProfile.raceIcons, subjectProfile.genderIcons],
      succeeded: subjectProfile.id !== undefined,
      max_profiles: userProfile.paidProfiles + userProfile.freeProfiles,
      profiles_remaining: remainingProfiles,
  };
  logEvent(getAnalytics(), 'account.created_profile', telemetryProps);
}

const _reportEditSubjectProfileTelemetry = (userProfile,  subjectProfile) => {
  const telemetryProps = {
      profile_uuid: userProfile.id,
      chosen_profile_icon: [subjectProfile.raceIcons, subjectProfile.genderIcons],
      succeeded: subjectProfile.id !== undefined
  };
  logEvent(getAnalytics(), 'account.created_profile', telemetryProps);
}

export const createProfile = async (cloudFunctions, userProfile, profile, reportCollections) => {
  const profileData = ProfileKeys.compress(profile, userProfile.id);
  profileData[ProfileKeys.sessionRunners] = [userProfile.id];

  const createProfileFunction = httpsCallable(cloudFunctions, CREATE_PROFILE_FUNCTION);
  try {
    const result = await createProfileFunction({ profileData });
    const data = result.data;
    profile.id = data.id;

    // An absent or empty collection is a supported state, not an error: files
    // exported before certificates were included carry incidents alone.
    const reports = Object.entries(reportCollections || {}).flatMap(
      ([reportType, reportCollection]) =>
        (reportCollection || []).map(report => ({ reportType, report }))
    );

    if (reports.length > 0) {
      // The reports belong to the id the server has just assigned, not to the
      // one recorded in the file they came from.
      reports.forEach(({ report }) => { report.profileId = profile.id; });
      const saved = await Promise.all(
        reports.map(({ reportType, report }) => saveReport(reportType, report))
      );

      // The profile itself exists by this point, so a partial failure reports
      // what did not make it rather than claiming the whole upload failed.
      const failures = saved.filter(success => !success).length;
      if (failures > 0) {
        console.error(`saveReport failed for ${failures} of ${saved.length} reports on profile ${profile.id}`);
        return { ...data, reportFailures: failures };
      }
    }
    _reportAddSubjectProfileTelemetry(userProfile, profile, data.remainingProfiles);
    return data;
  } catch (error) {
    _reportAddSubjectProfileTelemetry(userProfile, profile, error.remainingProfiles);
    return { error };
  }
};

export const editProfile = async (cloudFunctions, userProfile, profileChanges) => {
  const profileData = ProfileKeys.compressChanges(profileChanges);

  const editProfileFunction = httpsCallable(cloudFunctions, EDIT_PROFILE_FUNCTION);
  try {
    const result = await editProfileFunction({ profileData });
    const data = result.data;
    _reportEditSubjectProfileTelemetry(userProfile, profileData);
    return data;
  } catch (error) {
    _reportEditSubjectProfileTelemetry(userProfile, profileData);
    return { error };
  }
};

export const deleteProfile = async (cloudFunctions, profileId, user) => {
  const deleteProfile = httpsCallable(cloudFunctions, DELETE_PROFILE_FUNCTION);
  const result = await deleteProfile({profileId});
  const telemetryProps = {
    profile_uuid: profileId,
    max_profiles: user.paidProfiles + user.freeProfiles,
    profiles_remaining: result.remainingProfiles,
  };
  logEvent(getAnalytics(), 'account.deleted_profile', telemetryProps);
};

// Sets only what is needed to prevent a race condition
// overwriting other data.
export const setProfileConsent = (profile, consentObtained) => {
  return updateDoc(
    doc(getFirestore(), ProfileKeys.tableName, profile.id),
    { [ProfileKeys.consentObtained]: consentObtained }
  );
}